import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ModalImage from 'react-modal-image';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import LoopIcon from '@mui/icons-material/Loop';

import Modal from '../Modal.jsx';
import ImageForm from '../../components/Images/ImageForm.jsx';
import Loading from '../Loading.jsx';
import HeaderContainer from '../HeaderContainer.jsx';
import Pagination from '../../components/Custom-UI/Tables/Pagination.jsx';
import {
  Table,
  TableHead,
  TableHeader,
  TableRow,
  TableCell,
  TableBody,
  TableButton,
} from '../../components/Custom-UI/Tables/Elements.jsx';
import Select from '../Form/Select.jsx';
import Checkbox from '../Form/Checkbox.jsx';
import LearnUnitsCell from '../../components/LearnUnits/LearnUnitsCell.jsx';
import { useGetImages, deleteImage } from '../../models/images.js';
import { useGetLearnunits } from '../../models/learnunits.js';
import { getCurrentUser, useGetUsersWithPagination } from '../../models/user.js';

const imageType = {
  '2D-Image': 'Imagen 2D',
  '3D-Image': 'Imagen 3D',
};

function ImagesTable() {
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [page, setPage] = useState(1);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [learnunitsData, learnunitsDataLoading] = useGetLearnunits();
  const [learnunitsObject, setLearnunitsObject] = useState({});
  const [showImages, setShowImages] = useState(true);
  const [typeFilter, setTypeFilter] = useState('');
  const [learnunitFilter, setLearnunitFilter] = useState('');
  const [searchFilter, setSearchFilter] = useState('');
  const [imagesData, imagesDataLoading] = useGetImages(
    page,
    itemsPerPage,
    typeFilter,
    learnunitFilter,
    searchFilter
  );
  const navigate = useNavigate();
  const [pagination, setPagination] = useState({
    currentPage: page,
    itemsPerPage: itemsPerPage,
    totalPages: images.length,
  });
  const [open, setOpen] = useState(false);
  const [type, setType] = useState(null);
  const [method, setMethod] = useState(null);
  const [imageToEdit, setImageToEdit] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [teachers, teachersDataLoading] = useGetUsersWithPagination(1, 99999, 'TEACHER');
  const [teachersDict, setTeachersDict] = useState({});

  const options = [{ value: '', label: <em>Todas</em> }];
  if (learnunitsData) {
    learnunitsData?.data?.forEach(learnunit => {
      options.push({ value: learnunit.id, label: learnunit.name });
    });
  }

  useEffect(() => {
    if (imagesData && !imagesDataLoading) {
      setImages(imagesData.images);
      setPagination({
        currentPage: imagesData.pagination.currentPage,
        itemsPerPage: itemsPerPage,
        totalPages: imagesData.pagination.pages,
      });
      setLoading(false);
    }
  }, [imagesData, imagesDataLoading, page, itemsPerPage, typeFilter, learnunitFilter]);

  useEffect(() => {
    if (learnunitsData?.data && !learnunitsDataLoading) {
      for (let i = 0; i < learnunitsData.data.length; i++) {
        learnunitsObject[learnunitsData.data[i].id] = learnunitsData.data[i].name;
      }
      setLearnunitsObject(learnunitsObject);
    }
  }, [learnunitsData, learnunitsDataLoading]);

  useEffect(() => {
    getCurrentUser()
      .then(user => {
        setUserRole(user.role);
      })
      .catch();
  }, []);

  useEffect(() => {
    if (teachers?.users && !teachersDataLoading) {
      const teachersDictTemp = {};
      teachers?.users?.forEach(teacher => {
        teachersDictTemp[teacher.id] = `${teacher.firstnames} ${teacher.lastnames}`;
      });
      setTeachersDict(teachersDictTemp);
    }
  }, [teachers, teachersDataLoading]);

  function handleType(option) {
    setTypeFilter(option.target.value);
    setPage(1);
    setLoading(true);
  }

  function handleLearnunit(option) {
    setLearnunitFilter(option.target.value);
    setPage(1);
    setLoading(true);
  }

  function handleSearch(option) {
    setSearchFilter(option.target.value);
    setPage(1);
    setLoading(true);
  }

  const handleDelete = async ImageId => {
    const confirmation = window.confirm('¿Está seguro que desea eliminar la imagen?');
    if (confirmation) {
      const response = await deleteImage(ImageId);
      if (response.status === 200 || response.status === 201) {
        alert('Imagen eliminada con éxito.');
        window.location.reload();
      } else {
        alert('Error al eliminar la imagen. Intente de nuevo.');
      }
    }
  };

  return (
    <div className='antialiased font-sans'>
      <Modal open={open} setOpen={setOpen}>
        <ImageForm setOpen={setOpen} method={method} type={type} image={imageToEdit} />
      </Modal>
      <div className='w-full divide-y divide-primary'>
        <HeaderContainer
          title='Imágenes'
          search={{ handleSearch: handleSearch }}
          actions={[
            {
              title: 'Añadir imagen/es del escritorio',
              icon: <AddIcon />,
              handleClick: () => {
                setImageToEdit({});
                setType('desktop');
                setMethod('POST');
                setOpen(true);
              },
            },
            {
              title: 'Añadir imagen por URL',
              icon: <AddIcon />,
              handleClick: () => {
                setImageToEdit({});
                setType('url');
                setMethod('POST');
                setOpen(true);
              },
            },
            {
              title: 'Ver repetidas',
              icon: <LoopIcon />,
              handleClick: () => {
                navigate('/images/repeated-images');
              },
            },
          ]}
        />
        <div className='py-8 px-4 sm:px-8 min-w-full w-full'>
          <div className='flex justify-between items-center mb-4'>
            <div className='flex gap-4'>
              <Select
                name='learnunit'
                label='Unidad de Aprendizaje'
                value={learnunitFilter}
                onChange={handleLearnunit}
                options={options}
                sx={{ minWidth: '210px' }}
              />
              <Select
                name='type'
                label='Tipo de imagen'
                value={typeFilter}
                onChange={handleType}
                options={[
                  { value: '', label: <em>Todos</em> },
                  { value: '2D-Image', label: 'Imagen 2D' },
                  { value: '3D-Image', label: 'Imagen 3D' },
                ]}
                sx={{ minWidth: '160px' }}
              />
            </div>
            <Checkbox
              label='Ver imágenes'
              defaultChecked
              onChange={() => setShowImages(!showImages)}
            />
          </div>
          <Table>
            <TableHead>
              <TableRow>
                {showImages ? <TableHeader className='min-w-40'>Imagen</TableHeader> : null}
                <TableHeader>Nombre</TableHeader>
                <TableHeader>Unidad de aprendizaje</TableHeader>
                <TableHeader>Tipo</TableHeader>
                <TableHeader>URL</TableHeader>
                {userRole === 'ADMIN' ? <TableHeader>Propietario</TableHeader> : null}
                <TableHeader />
              </TableRow>
            </TableHead>
            <TableBody>
              {imagesDataLoading || loading ? (
                <TableRow>
                  <TableCell colSpan='6'>
                    <div className='flex justify-center items-center h-full'>
                      <Loading />
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                images.map(image => (
                  <TableRow key={image.id}>
                    {showImages ? (
                      <TableCell>
                        <ModalImage
                          className='w-40'
                          small={image.url}
                          large={image.url}
                          alt={image.details}
                          hideZoom
                          hideDownload
                        />
                      </TableCell>
                    ) : null}
                    <TableCell>{image.details}</TableCell>
                    <TableCell>
                      <LearnUnitsCell learnUnits={image.learnUnits} />
                    </TableCell>
                    <TableCell>{imageType[image.type]}</TableCell>
                    <TableCell>
                      <a href={image.url} target='_blank' rel='noreferrer'>
                        <u>URL</u>
                      </a>
                    </TableCell>
                    {userRole === 'ADMIN' ? (
                      <TableCell>{teachersDict[image.owner_id]}</TableCell>
                    ) : null}
                    <TableCell>
                      <div className='flex w-full justify-between gap-2 items-center'>
                        <TableButton
                          variant={'edit'}
                          onClick={() => {
                            setImageToEdit(image);
                            setType('url');
                            setMethod('PATCH');
                            setOpen(true);
                          }}>
                          <EditOutlinedIcon />
                        </TableButton>
                        <TableButton variant={'delete'} onClick={() => handleDelete(image.id)}>
                          <DeleteOutlinedIcon color='red' />
                        </TableButton>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <Pagination
            pagination={pagination}
            setPage={setPage}
            setLoading={setLoading}
            setItemPerPage={setItemsPerPage}
          />
        </div>
      </div>
    </div>
  );
}

export default ImagesTable;
